import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box, Dialog, DialogContent,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  Stack,
  DialogTitle
} from '@mui/material';

import {
  AddOutlined,
  CloseOutlined,
  KeyboardArrowDown
} from '@mui/icons-material';

import {
  practiceAddFieldButtonSx, practiceContactFieldSx
} from './styles';
import {
  dialogHeaderStyles,
  textFieldSx,
  menuProps
} from '../../../Common/components/Styles/InputStyles';
import CommonDialogActions from '../../../Common/components/CommonDialogActions';

const states = [
  'Arizona',
  'California',
  'Florida',
  'New York',
  'Texas',
  'Washington'
];

const initialForm = {
  billingAddress: '',
  city: '',
  email: '',
  facilityNpi: '',
  fax: '',
  mobile: '',
  practiceName: '',
  state: '',
  zip: ''
};

const PracticeInformationModal = ({
  mode,
  onClose,
  onSubmit,
  open,
  practice
}) => {
  const [form, setForm] = useState(initialForm);

  const isEditMode = mode === 'edit';

  useEffect(() => {
    if (isEditMode && practice) {
      setForm({
        billingAddress: practice.billingAddress || '',
        city: practice.city || '',
        email: practice.email || '',
        facilityNpi: practice.facilityNpi || '',
        fax: practice.fax || '',
        mobile: practice.mobile || practice.phone || '',
        practiceName: practice.practiceName || '',
        state: practice.state || '',
        zip: practice.zip || ''
      });

      return;
    }

    setForm(initialForm);
  }, [isEditMode, open, practice]);

  const handleChange = field => event => {
    setForm(previousForm => ({
      ...previousForm,
      [field]: event.target.value
    }));
  };

  const handleSubmit = () => {
    if (!form.practiceName.trim()) {
      return;
    }

    onSubmit({
      ...form,
      address: [form.billingAddress, form.city, form.state, form.zip]
        .filter(Boolean)
        .join(', '),
      phone: form.mobile
    });
  };

  const isSubmitDisabled = !form.practiceName.trim();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: '10px',
            overflow: 'hidden'
          }
        }
      }}>
      <DialogTitle sx={dialogHeaderStyles.dialogheader}>
        <Box>
          <Typography sx={dialogHeaderStyles.title}>
            {isEditMode
              ? 'Update Practice Information'
              : 'Add Practice Information'}
          </Typography>
          <Typography sx={dialogHeaderStyles.subtitle}>
           {isEditMode
              ? 'View and update your practice information'
              : 'Create a new practice.'}
          </Typography>
        </Box>

        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={dialogHeaderStyles.dialogheaderclose}>
          <CloseOutlined sx={{ fontSize: 22 }} />
        </IconButton>
      </DialogTitle>
   

     <DialogContent sx={{ px: 2 }}>
        <Stack spacing={1.6} sx={{ pt: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Practice Name"
            onChange={handleChange('practiceName')}
            placeholder={isEditMode ? '' : 'Practice Name'}
            required
            sx={textFieldSx}
            value={form.practiceName}
          />

          <TextField
            fullWidth
            size="small"
            label="Facility NPI Number"
            onChange={handleChange('facilityNpi')}
            placeholder="Facility NPI Number"
            sx={textFieldSx}
            value={form.facilityNpi}
          />

          <TextField
            fullWidth
            size="small"
            label="Billing Address-Street/P.O Box"
            onChange={handleChange('billingAddress')}
            placeholder="Billing Address-Street/P.O Box"
            sx={textFieldSx}
            value={form.billingAddress}
          />

          <TextField
            fullWidth
            size="small"
            label="City"
            onChange={handleChange('city')}
            placeholder="City"
            sx={textFieldSx}
            value={form.city}
          />

          <FormControl fullWidth size="small" sx={textFieldSx}>
            <InputLabel>State</InputLabel>

            <Select
              label="State"
              value={form.state}
              onChange={handleChange('state')}
              MenuProps={menuProps}
              IconComponent={KeyboardArrowDown}>
              {states.map(state => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            size="small"
            label="Zip"
            onChange={handleChange('zip')}
            placeholder="Zip"
            sx={textFieldSx}
            value={form.zip}
          />

          <Box sx={practiceContactFieldSx}>
            <TextField
              fullWidth
              size="small"
              label="Mobile Number"
              onChange={handleChange('mobile')}
              placeholder="Mobile Number"
              sx={textFieldSx}
              value={form.mobile}
            />

            <IconButton
              aria-label="Add mobile number"
              sx={practiceAddFieldButtonSx}>
              <AddOutlined sx={{fontSize:16}}/>
            </IconButton>
          </Box>

          <Box sx={practiceContactFieldSx}>
            <TextField
              fullWidth
              size="small"
              label="Fax Number"
              onChange={handleChange('fax')}
              placeholder="Fax Number"
              sx={textFieldSx}
              value={form.fax}
            />

            <IconButton
              aria-label="Add fax number"
              sx={practiceAddFieldButtonSx}>
              <AddOutlined sx={{fontSize:16}}/>
            </IconButton>
          </Box>

          <TextField
            fullWidth
            size="small"
            label="Email"
            onChange={handleChange('email')}
            placeholder="Email"
            sx={textFieldSx}
            type="email"
            value={form.email}
          />
        </Stack>
      </DialogContent>
       <CommonDialogActions
              onCancel={onClose}
              onSubmit={handleSubmit}
              submitText={isEditMode ? 'Save' : 'Add Practice'}
              disabled={isSubmitDisabled}
            />

     
    </Dialog>
  );
};

PracticeInformationModal.defaultProps = {
  practice: null
};

PracticeInformationModal.propTypes = {
  mode: PropTypes.oneOf(['add', 'edit']).isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  practice: PropTypes.shape({
    address: PropTypes.string,
    billingAddress: PropTypes.string,
    city: PropTypes.string,
    email: PropTypes.string,
    facilityNpi: PropTypes.string,
    fax: PropTypes.string,
    id: PropTypes.number,
    mobile: PropTypes.string,
    phone: PropTypes.string,
    practiceName: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string
  })
};

export default PracticeInformationModal;
